import {gql} from 'apollo-angular';
import { Page } from '../../entities/page/page';
import { EntityLink } from '../../entities/entityLink/entityLink';
import { LinkedEntityConnection } from '../../entities/entityLink/linkedEntityConnection';

export const TASK_FRAGMENT = gql`
  fragment TaskFields on Task {
    id
    description
    number
    type
    taskType {
      code
      name
      prefix
      icon
      color
    }
    state
    color
    title
    dueDateTimePeriod {
      modifier
      dateTime
      fromDateTime
      toDateTime
      precision
    }
    completedBy {
      id
      firstName
      lastName
    }
    completedDateTime
    lastModifiedBy {
      id
      firstName
      lastName
    }
    lastModifiedDateTime
    createdBy {
      id
      firstName
      lastName
    }
    createdDateTime
    assignment {
      assigneeId
      assignmentType
      typeLabel
      name
      dueDateTimePeriod {
        modifier
        dateTime
        fromDateTime
        toDateTime
      }
    }
    commentCount
    fileCount
    linkedOrders: linkedEntities(entityType: "Order") {
      linkId
      linkedEntity {
        ... on Order {
          id
          number
          note
        }
      }
    }
    linkedResources: linkedEntities(entityType: "Resource") {
      linkId
      linkedEntity {
        ... on Resource {
          id
          text
        }
      }
    }
    linkedContacts: linkedEntities(entityType: "Contact") {
      linkId
      linkedEntity {
        ... on Contact {
          id
          text
        }
      }
    }
    address {
      id
      country
      city
      postalCode
      street
      houseNumber
      name
      lat
      lng
      text
      type
      note
      geocodingResultGrade
      ref
    }
  }
`;

export const ISSUE_FRAGMENT = gql`
  fragment IssueFields on Issue {
    id
    title
    number
    dueDateTime
    assignee {
      id
      firstName
      lastName
    }
    labels {
      name
      color
      scope
    }
    createdBy {
      id
      firstName
      lastName
    }
    createdDateTime
    lastModifiedDateTime
    noteCount
    fileCount
    issueStatus {
      id
      code
      label
      roles
      color
      statusGroup {
        code
        binaryStatusLabel
        isOpen
      }
    }
    issueType {
      code
      name
      prefix
      icon
      color
    }
  }
`;

export const RESOURCE_FRAGMENT = gql`
  fragment ResourceFields on Resource {
    id
    # Add more Resource fields as needed
  }
`;


export const GET_ENTITY_LINKS_QUERY = gql`
query getEntityLinksPaged($outwardEntityId: UUID, $inwardEntityType: String)
{
  getEntityLinksPaged: getEntityLinksPaged(
    filter: {
      outwardEntityId: $outwardEntityId,
      inwardEntityType: $inwardEntityType,
      sortRequest: { sortProperties: ["id"], sortDirection: "asc" },
      pagination: { number: 0, size: 10,  sortProperty: "id", sortDirection: "asc"}
    }
  ){
    totalPages
    totalElements
    number
    size
    content{
      id
      outwardEntityId
      outwardEntityType
      inwardEntityId
      inwardEntityType
      linkType
      linkable{
        id
        entityType
        text
        ... on Task {
          id
          description
          number
          type
          taskType {
            code
            name
            prefix
            icon
            color
          }
          state
          color
          title
          dueDateTimePeriod {
            modifier
            dateTime
            fromDateTime
            toDateTime
            precision
          }
          completedBy {
            id
            firstName
            lastName
          }
          completedDateTime
          lastModifiedBy {
            id
            firstName
            lastName
          }
          lastModifiedDateTime
          createdBy {
            id
            firstName
            lastName
          }
          createdDateTime
          assignment {
            assigneeId
            assignmentType
            typeLabel
            name
            dueDateTimePeriod{
                modifier
                dateTime
                fromDateTime
                toDateTime
            }
          }
          commentCount
          fileCount
        }
        ... on Resource {
            id
        }
        ... on Issue {
          id
          title
          number
          dueDateTime
          assignee{
            id
            firstName
            lastName
          }
          labels{
            name
            color
            scope
          }
          assignee{
            id
            firstName
            lastName
          }
          createdBy{
            id
            firstName
            lastName
          }
          createdDateTime
          lastModifiedDateTime
          noteCount
          fileCount
          issueStatus{
            id
            code
            label
            roles
            color
            statusGroup{
                code
                binaryStatusLabel
                isOpen
            }
          }
          issueType{
            code
            name
            prefix
            icon
            color
          }
        }
      }
    }
  }
}
`;

export interface getEntityLinksPagedResponse {
  getEntityLinksPaged: Page<EntityLink>;
}

export const GET_LINKED_ENTITIES_QUERY = gql`
  ${TASK_FRAGMENT}
  ${ISSUE_FRAGMENT}
  ${RESOURCE_FRAGMENT}

  query getLinkedEntities(
    $entityId: UUID!
    $linkedEntityType: String
    $linkType: EntityLinkType
    $pageNumber: Int = 0
    $pageSize: Int = 10
    $sortProperties: [String!] = ["id"]
    $sortDirection: String = "asc"
  ) {
    getLinkedEntities(
      entityId: $entityId
      linkedEntityType: $linkedEntityType
      linkType: $linkType
      pagination: {
        number: $pageNumber
        size: $pageSize
      }
      sortRequest: {
        sortProperties: $sortProperties
        sortDirection: $sortDirection
      }
    ) {
      totalPages
      totalElements
      number
      size
      content {
        linkId
        linkType
        perspective
        linkLabel
        role
        linkedEntity {
          id
          entityType
          text
          __typename
          ...TaskFields
          ...IssueFields
          ...ResourceFields
        }
      }
    }
  }
`;

export interface GetLinkedEntitiesVariables {
  entityId: string;
  linkedEntityType?: string;
  linkType?: string;
  pageNumber?: number;
  pageSize?: number;
  sortProperties?: string[];
  sortDirection?: 'asc' | 'desc';
}

export interface GetLinkedEntitiesResponse {
  getLinkedEntities: Page<LinkedEntityConnection>;
}
